//@flow
import * as React from 'react';
import {
    PieChart as RechartsPieChart,
    Pie as RechartsPie,
    Cell as RechartsCell,
    Tooltip as RechartsTooltip,
    Legend as RechartsLegend
} from 'recharts';
import { type ThemeType } from 'react-css-themr';
import * as Immutable from 'immutable';
import NumberUtils from 'common/utils/NumberUtils';

import PjpChartsCustomLegend from './PjpChartsCustomLegend';
import PjpPieChartTooltip from './PjpPieChartTooltip';
import { type PjpPolarChartDataType } from 'common/entity/pjpMaster/pjpDataTypes/insights/PjpPolarChartDataType';
import DifferentiableColorPicker from 'common/utils/DifferentiableColorPicker';

import { memoized } from 'common/utils/Memoizer';
import ChartUtils from 'common/utils/ChartUtils';
import { DistinctColors } from 'common/enums/ColorEnum';

type Props = {
    width: number,
    height: number,
    customHeight: number,
    existingPlan?: PjpPolarChartDataType,
    currentPlan?: PjpPolarChartDataType,
    unit: string,
    theme: ThemeType,
    legendProps?: {
        hideLegendValue: boolean,
        align: string,
        layout: string,
        verticalAlign: string,
        wrapperStyle: {
            width: string
        },
        key?: string
    }
};

type State = {
    hoveredCell: ?PieChartCellItem
};

type PieChartCellItem = {
    name: string,
    value: string,
    innerRadius: number,
    outerRadius: number,
    startAngle: number,
    endAngle: number,
    payload: RechartsPieChartPayloadType,
    cx: number
};

type RechartsPieChartPayloadType = {
    name: string,
    value: string,
    index: number,
    color: string,
    percent: number
};

const RADIAN = Math.PI / 180;
const [DEFAULT_COLOR] = DistinctColors;

export default class PjpPieChart extends React.PureComponent<Props, State> {
    state: State = { hoveredCell: null, hoveredIndex: null };

    componentDidMount() {
        this.isAnimationActive = false;
    }

    isAnimationActive: boolean = true;

    @memoized()
    _getFormattedData(data) {
        return data.map((item, index) => {
            const { value } = item;
            const { metricUnitType, unitConverter } = this.props;
            let convertedValue;
            if (metricUnitType === 'DISTANCE') {
                convertedValue = unitConverter.convertMeters({
                    value,
                    pluralizeUnit: true,
                    roundOff: true,
                    decimals: 2,
                    showUnitAbbr: false,
                    padLeft: true
                });
            } else if (metricUnitType === 'TIME') {
                convertedValue = unitConverter.convertSeconds({
                    value
                });
            } else {
                convertedValue = { value, unit: null };
            }
            const formattedUnit = convertedValue.unit;
            const formattedValue = NumberUtils.toNumber(convertedValue.value);
            return {
                index,
                name: item.name,
                value: formattedValue,
                dataKey: item.dataKey,
                dataValue: formattedValue,
                color: item.color || DEFAULT_COLOR,
                percent: ChartUtils.getPiePercentage(data, item),
                [item.dataKey]: formattedValue,
                unit: formattedUnit
            };
        });
    }

    getColorsForExistingBeat(currentPlan, existingPlan) {
        const colorPicker = new DifferentiableColorPicker();
        const currentPlanColorMap = currentPlan.reduce((acc, item) => {
            return acc.set(item.name, item.color);
        }, new Immutable.Map());
        const updatedExistingPlan = existingPlan.map(item => {
            if (currentPlanColorMap.has(item.name)) {
                item.color = colorPicker.lighten(
                    currentPlanColorMap.get(item.name),
                    0.3
                );
            }
            return item;
        });
        return updatedExistingPlan;
    }

    @memoized()
    _getLegendData(data) {
        //merge legends here
        return data.map((item, index) => {
            //TODO: Format values for different data types
            return {
                index,
                name: item.name,
                value: item.value,
                color: item.color || DEFAULT_COLOR
            };
        });
    }

    _getHoverInfo() {
        const { hoveredCell } = this.state;
        if (!hoveredCell) {
            return null;
        }

        const formattedData = this._getFormattedData(
            this.props.currentPlan || this.props.existingPlan
        );
        const cellIndex = hoveredCell.payload.index;
        const updatedData = formattedData[cellIndex];
        const { cx } = hoveredCell;

        return {
            cellIndex,
            cell: hoveredCell,
            color: updatedData?.color,
            dataSet: [updatedData],
            cx
        };
    }

    _onPieEnter = (cellItem: PieChartCellItem) => {
        const { hoveredCell } = this.state;
        if (hoveredCell?.payload.index !== cellItem.payload.index) {
            this.setState({
                hoveredCell: cellItem
            });
        }
    };

    _onPieExit = (cellItem: PieChartCellItem) => {
        const { hoveredCell } = this.state;
        if (hoveredCell?.payload.index === cellItem.payload.index) {
            this.setState({ hoveredCell: null });
            return;
        }
    };

    _getPieRadius = (width: number, height: number) => {
        const pieWidth = width / 8; //width of the donut
        const maxPieInnerRadius = height / 2 - pieWidth - 20; // 10 px padding, 5 on both sides
        let innerRadius = width / 10;

        // pie radius calculation
        if (innerRadius >= maxPieInnerRadius) {
            innerRadius = maxPieInnerRadius;
        }
        const outerRadius = innerRadius + pieWidth;
        return { innerRadius, outerRadius };
    };

    _customLabelRenderer = pieProps => {
        const { theme } = this.props;
        return this._renderCustomizedLabel({ theme, ...pieProps });
    };

    _renderCustomizedLabel({
        percent,
        cx,
        cy,
        startAngle,
        endAngle,
        midAngle,
        innerRadius,
        outerRadius
    }) {
        if (Math.abs(startAngle - endAngle) < 10) {
            return [];
        }
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text
                x={x}
                y={y}
                fontWeight={600}
                fontSize={9}
                fill="white"
                textAnchor={'middle'}
                dominantBaseline="central"
                style={{ pointerEvents: 'none' }}
            >
                {`${percent.toFixed(0)}%`}
            </text>
        );
    }

    render() {
        const {
            height,
            width,
            currentPlan,
            existingPlan,
            unit,
            legendProps,
            customHeight
        } = this.props;
        const updatedExistingBeatWithColors =
            existingPlan && currentPlan
                ? this.getColorsForExistingBeat(currentPlan, existingPlan)
                : existingPlan;
        const currentPlanFormattedData = currentPlan
            ? this._getFormattedData(currentPlan)
            : null;
        const existingPlanFormattedData = existingPlan
            ? this._getFormattedData(updatedExistingBeatWithColors)
            : null;
        const { innerRadius, outerRadius } = this._getPieRadius(width, height);
        const hoverInfo = this._getHoverInfo();

        const pieX = currentPlan && existingPlan ? width / 4 : width / 2;
        const pieY = height / 2 - 20;

        return (
            <RechartsPieChart width={width} height={height + customHeight || 0}>
                {currentPlan ? (
                    <RechartsPie
                        data={currentPlanFormattedData}
                        cx={pieX}
                        cy={pieY}
                        activeIndex={hoverInfo ? [hoverInfo.cellIndex] : []}
                        isAnimationActive={this.isAnimationActive}
                        labelLine={false}
                        label={this._customLabelRenderer}
                        innerRadius={innerRadius}
                        outerRadius={outerRadius}
                        onMouseOver={this._onPieEnter}
                    >
                        {currentPlanFormattedData.map(entry => {
                            return (
                                <RechartsCell
                                    key={entry.name}
                                    fill={entry.color}
                                    strokeWidth={0}
                                />
                            );
                        })}
                    </RechartsPie>
                ) : null}
                {existingPlan ? (
                    <RechartsPie
                        data={existingPlanFormattedData}
                        cx={currentPlan ? pieX * 3 : pieX}
                        cy={pieY}
                        activeIndex={hoverInfo ? [hoverInfo.cellIndex] : []}
                        isAnimationActive={this.isAnimationActive}
                        labelLine={false}
                        label={this._customLabelRenderer}
                        innerRadius={innerRadius}
                        outerRadius={outerRadius}
                        onMouseOver={this._onPieEnter}
                    >
                        {existingPlanFormattedData.map(entry => {
                            return (
                                <RechartsCell
                                    key={entry.name}
                                    fill={entry.color}
                                    strokeWidth={0}
                                />
                            );
                        })}
                    </RechartsPie>
                ) : null}

                {hoverInfo?.dataSet ? (
                    <RechartsPie
                        data={hoverInfo.dataSet}
                        cx={hoverInfo.cx - 5}
                        cy={pieY}
                        activeIndex={0}
                        isAnimationActive={false}
                        labelLine={false}
                        label={this._customLabelRenderer}
                        innerRadius={innerRadius}
                        outerRadius={outerRadius}
                        startAngle={hoverInfo.cell.startAngle}
                        endAngle={hoverInfo.cell.endAngle}
                        onMouseLeave={this._onPieExit}
                    >
                        <RechartsCell
                            fill={hoverInfo.color}
                            stroke={'#ffffff'}
                            strokeWidth={4}
                        />
                    </RechartsPie>
                ) : null}
                <RechartsTooltip
                    filterNull={false}
                    content={<PjpPieChartTooltip unit={unit} />}
                />
                <RechartsLegend
                    wrapperStyle={{ width }}
                    align={legendProps?.align ? legendProps.align : 'left'}
                    verticalAlign={
                        legendProps?.verticalAlign
                            ? legendProps.verticalAlign
                            : 'middle'
                    }
                    layout="horizontal"
                    payload={this._getLegendData(
                        currentPlanFormattedData || existingPlanFormattedData
                    )}
                    content={<PjpChartsCustomLegend />}
                />
            </RechartsPieChart>
        );
    }
}
